/* eslint-disable react/jsx-props-no-spreading */

import { useRef, useState } from 'react';
import { cn } from 'utils/classNamesTailwind';

type FixedPlacement = 'right' | 'left' | 'top' | 'bottom';
type TooltipPlacement = 'cursor' | 'auto' | FixedPlacement;

const AUTO_PLACEMENT_VIEWPORT_GAP = 12;

function Tooltip({
  className = '',
  children,
  tooltipContent = '',
  delay = 'delay-100',
  placement = 'left',
  arrow = true,
  inline = false,
  onClick,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  tooltipContent?: React.ReactNode;
  delay?: string;
  placement?: TooltipPlacement;
  arrow?: boolean;
  inline?: boolean;
}) {
  const isCursorPlacement = placement === 'cursor';
  const isAutoPlacement = placement === 'auto';

  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [autoResolvedPlacement, setAutoResolvedPlacement] = useState<FixedPlacement>('right');
  const [isSuppressed, setIsSuppressed] = useState(false);

  const resolvedPlacement: FixedPlacement | undefined = isCursorPlacement
    ? undefined
    : isAutoPlacement
      ? autoResolvedPlacement
      : placement;

  const handleMouseEnter = () => {
    if (!isAutoPlacement || !wrapperRef.current) {
      return;
    }
    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    const tooltipWidth = tooltipRect?.width ?? 0;
    const tooltipHeight = tooltipRect?.height ?? 0;

    const availableSpace: Record<FixedPlacement, number> = {
      right: window.innerWidth - triggerRect.right,
      left: triggerRect.left,
      top: triggerRect.top,
      bottom: window.innerHeight - triggerRect.bottom,
    };

    const preferenceOrder: FixedPlacement[] = ['right', 'top', 'left', 'bottom'];
    const requiredSpace = (candidate: FixedPlacement) =>
      (candidate === 'left' || candidate === 'right' ? tooltipWidth : tooltipHeight) + AUTO_PLACEMENT_VIEWPORT_GAP;

    const fittingPlacement = preferenceOrder.find(candidate => availableSpace[candidate] >= requiredSpace(candidate));
    const roomiestPlacement = preferenceOrder.reduce((best, candidate) =>
      availableSpace[candidate] > availableSpace[best] ? candidate : best
    );

    setAutoResolvedPlacement(fittingPlacement ?? roomiestPlacement);
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        `group/tooltip ${inline ? 'inline-flex' : 'flex'} align-baseline text-inherit [text-decoration:inherit]`,
        { relative: !isCursorPlacement },
        className
      )}
      {...props}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsSuppressed(false)}
      onClick={e => {
        setIsSuppressed(true);
        onClick?.(e);
      }}
      onMouseMove={
        isCursorPlacement
          ? e => {
              const tooltipElement = e.currentTarget.querySelector<HTMLDivElement>('.tooltip');
              if (tooltipElement) {
                tooltipElement.style.position = 'fixed';
                tooltipElement.style.top = `${e.clientY}px`;
                tooltipElement.style.left = `${e.clientX}px`;
                tooltipElement.style.transform = '';
              }
            }
          : undefined
      }
    >
      {children}
      <div
        ref={tooltipRef}
        className={cn(
          `tooltip opacity-0 transition-opacity duration-300 font-semibold
           group-hover/tooltip:opacity-100 pointer-events-none bg-[#1a1a1a] text-[#fdfdfd] shadow-lg
           rounded-md border border-gray-200 px-2 py-1 text-[13px] max-w-xs z-50 whitespace-nowrap ${delay}`,
          {
            'fixed translate-x-3 translate-y-3': isCursorPlacement,
            'absolute left-full top-1/2 -translate-y-1/2 ml-3': resolvedPlacement === 'right',
            'absolute right-full top-1/2 -translate-y-1/2 mr-3': resolvedPlacement === 'left',
            'absolute bottom-full left-1/2 -translate-x-1/2 mb-2': resolvedPlacement === 'top',
            'absolute top-full left-1/2 -translate-x-1/2 mt-2': resolvedPlacement === 'bottom',
            '!opacity-0': isSuppressed,
          }
        )}
      >
        {tooltipContent}
        {arrow && resolvedPlacement === 'right' && (
          <span className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[8px] border-b-[8px] border-r-[10px] border-transparent border-r-[#1a1a1a]" />
        )}
        {arrow && resolvedPlacement === 'left' && (
          <span className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[8px] border-b-[8px] border-l-[10px] border-transparent border-l-[#1a1a1a]" />
        )}
        {arrow && resolvedPlacement === 'top' && (
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-transparent border-t-[#1a1a1a]" />
        )}
        {arrow && resolvedPlacement === 'bottom' && (
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[10px] border-transparent border-b-[#1a1a1a]" />
        )}
      </div>
    </div>
  );
}

export { Tooltip };
